import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Avatar,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Icon,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useBreakpointValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber
} from '@chakra-ui/react';
import {
  FiUsers,
  FiAward
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { useDataStore } from '../../contexts/DataStoreContext';
import { UserProfile } from './UserProfile';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { FaTrophy } from 'react-icons/fa';

export const Leaderboard = () => {
  const { user } = useAuth();
  const dataStore = useDataStore();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('predictions');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRank, setUserRank] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    loadLeaderboard();
    if (user) {
      loadUserRank();
    }
  }, [user]);

  useEffect(() => {
    sortLeaderboard();
  }, [sortBy]);

  // Reload leaderboard when users or votes change in DataStore
  useEffect(() => {
    if (!dataStore.isLoading && dataStore.userCount >= 0 && dataStore.voteCount >= 0) {
      loadLeaderboard();
    }
  }, [dataStore.users, dataStore.votes, dataStore.isLoading, dataStore.userCount, dataStore.voteCount]);

  const loadLeaderboard = () => {
    try {
      setLoading(true);

      // Wait for DataStore to be loaded before accessing data
      if (dataStore.isLoading) {
        return;
      }

      // Get leaderboard data directly from DataStore (computed from in-memory data)
      // DataStore returns data sorted by correct predictions by default
      const sortedData = dataStore.getLeaderboard(100);

      setLeaderboard(sortedData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserRank = () => {
    try {
      if (dataStore.isLoading) {
        return;
      }
      const rank = dataStore.getUserRank(user.uid);
      setUserRank(rank);
    } catch (error) {
      console.error('Error loading user rank:', error);
    }
  };

  const sortLeaderboard = () => {
    const sorted = [...leaderboard].sort((a, b) => {
      switch (sortBy) {
        case 'accuracy':
          if (a.accuracy !== b.accuracy) {
            return b.accuracy - a.accuracy;
          }
          return b.totalVotes - a.totalVotes;
        case 'totalVotes':
          return b.totalVotes - a.totalVotes;
        case 'events':
          return b.eventsParticipated - a.eventsParticipated;
        case 'predictions':
          return b.correctPredictions - a.correctPredictions;
        default:
          return 0;
      }
    });
    setLeaderboard(sorted);
  };

  const handleUserClick = (userData) => {
    setSelectedUser(userData);
    onOpen();
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Icon as={FaTrophy} color="yellow.400" boxSize={6} />;
      case 2:
        return <Icon as={FiAward} color="gray.300" boxSize={5} />;
      case 3:
        return <Icon as={FiAward} color="orange.400" boxSize={5} />;
      default:
        return <Text fontWeight="bold" color="gray.400" fontSize="lg">#{rank}</Text>;
    }
  };

  const getRankBadge = (rank) => {
    if (rank <= 3) {
      const colors = { 1: 'yellow', 2: 'gray', 3: 'orange' };
      return <Badge colorScheme={colors[rank]} variant="solid">Top {rank}</Badge>;
    }
    if (rank <= 10) {
      return <Badge colorScheme="blue" variant="solid">Top 10</Badge>;
    }
    if (rank <= 25) {
      return <Badge colorScheme="green" variant="outline">Top 25</Badge>;
    }
    return null;
  };

  const getAccuracyColor = (percentage) => {
    if (percentage >= 70) return 'green.400';
    if (percentage >= 50) return 'yellow.400';
    return 'red.400';
  };

  // Mobile Card Component for each leaderboard entry
  const MobileLeaderboardCard = ({ userData, rank, isCurrentUser }) => (
    <Card
      cursor="pointer"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      bg={isCurrentUser ? 'blue.900' : 'dark.700'}
      borderColor={isCurrentUser ? 'blue.600' : 'gray.600'}
      onClick={() => handleUserClick(userData)}
      transition="all 0.2s"
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          {/* Top Row: Rank, Avatar, Name */}
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              {getRankIcon(rank)}
              <Avatar size="md" name={userData.displayName} />
              <VStack align="start" spacing={0}>
                <Text
                  fontWeight="bold"
                  color={isCurrentUser ? 'blue.200' : 'white'}
                  fontSize="md"
                >
                  {userData.displayName}
                  {isCurrentUser && (
                    <Badge ml={2} size="sm" colorScheme="blue">
                      You
                    </Badge>
                  )}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  Since {userData.joinDate?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short'
                  })}
                </Text>
              </VStack>
            </HStack>
            {getRankBadge(rank)}
          </HStack>

          {/* Accuracy Section - Most Important */}
          <Box>
            <HStack justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" color="gray.300" fontWeight="medium">
                Accuracy
              </Text>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color={getAccuracyColor(userData.accuracy)}
              >
                {userData.accuracy.toFixed(1)}%
              </Text>
            </HStack>
            <Progress
              value={userData.accuracy}
              colorScheme={userData.accuracy >= 70 ? 'green' : userData.accuracy >= 50 ? 'yellow' : 'red'}
              size="md"
              borderRadius="md"
            />
          </Box>

          {/* Stats Grid */}
          <SimpleGrid columns={3} spacing={3}>
            <Stat size="sm">
              <StatLabel color="gray.400" fontSize="xs">
                Total Votes
              </StatLabel>
              <StatNumber color="white" fontSize="md">
                {userData.totalVotes}
              </StatNumber>
            </Stat>
            <Stat size="sm">
              <StatLabel color="gray.400" fontSize="xs">
                Correct
              </StatLabel>
              <StatNumber color="green.400" fontSize="md">
                {userData.correctPredictions}
              </StatNumber>
            </Stat>
            <Stat size="sm">
              <StatLabel color="gray.400" fontSize="xs">
                Events
              </StatLabel>
              <StatNumber color="gray.300" fontSize="md">
                {userData.eventsParticipated}
              </StatNumber>
            </Stat>
          </SimpleGrid>
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading) {
    return <LoadingSpinner message="Loading leaderboard..." />;
  }

  return (
    <Box maxW="6xl" mx="auto" p={{ base: 4, md: 6 }}>
      {/* Header */}
      <VStack spacing={6} mb={8}>
        <Heading size="xl" textAlign="center" color="white">
          <Icon as={FaTrophy} mr={3} color="yellow.400" />
          Leaderboard
        </Heading>

        {/* User's Rank Card */}
        {user && userRank && (
          <Card w="full" bg="blue.900" borderColor="blue.700">
            <CardBody>
              <HStack justify="space-between">
                <HStack spacing={4}>
                  <Avatar size="md" name={user.displayName || 'You'} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color="white">
                      {user.displayName || 'Your Ranking'}
                    </Text>
                    <Text fontSize="sm" color="blue.200">
                      Your current position
                    </Text>
                  </VStack>
                </HStack>

                <HStack spacing={4}>
                  <VStack spacing={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="white">
                      #{userRank.rank}
                    </Text>
                    <Text fontSize="xs" color="blue.200">
                      of {userRank.totalUsers}
                    </Text>
                  </VStack>

                  {userRank.percentile && (
                    <VStack spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="blue.300">
                        Top {userRank.percentile.toFixed(1)}%
                      </Text>
                      <Text fontSize="xs" color="blue.200">
                        Percentile
                      </Text>
                    </VStack>
                  )}
                </HStack>
              </HStack>
            </CardBody>
          </Card>
        )}

        {/* Sort Controls - Temporarily commented out */}
        {/* <Card w="full">
          <CardBody>
            <HStack justify="space-between">
              <Text color="gray.300">Sort by:</Text>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                maxW="200px"
              >
                <option value="accuracy">Accuracy</option>
                <option value="totalVotes">Total Votes</option>
                <option value="events">Events Participated</option>
                <option value="predictions">Correct Predictions</option>
              </Select>
            </HStack>
          </CardBody>
        </Card> */}
      </VStack>

      {/* Leaderboard Section */}
      <Card>
        <CardHeader>
          <Heading size="md" color="white">
            <Icon as={FiUsers} mr={2} />
            Top Predictors
          </Heading>
        </CardHeader>
        <CardBody>
          {leaderboard.length > 0 ? (
            <>
              {/* Mobile Card Layout */}
              {isMobile ? (
                <VStack spacing={4} align="stretch">
                  {leaderboard.map((userData, index) => {
                    const rank = index + 1;
                    const isCurrentUser = user && userData.userId === user.uid;

                    return (
                      <MobileLeaderboardCard
                        key={userData.userId}
                        userData={userData}
                        rank={rank}
                        isCurrentUser={isCurrentUser}
                      />
                    );
                  })}
                </VStack>
              ) : (
                /* Desktop Table Layout */
                <TableContainer>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Rank</Th>
                        <Th color="gray.300">User</Th>
                        <Th color="gray.300" isNumeric>Correct</Th>
                        <Th color="gray.300" isNumeric>Accuracy</Th>
                        <Th color="gray.300" isNumeric>Total Votes</Th>
                        <Th color="gray.300" isNumeric>Events</Th>
                        <Th color="gray.300">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {leaderboard.map((userData, index) => {
                        const rank = index + 1;
                        const isCurrentUser = user && userData.userId === user.uid;

                        return (
                          <Tr
                            key={userData.userId}
                            cursor="pointer"
                            _hover={{ bg: 'dark.600' }}
                            bg={isCurrentUser ? 'blue.900' : 'transparent'}
                            onClick={() => handleUserClick(userData)}
                          >
                            <Td>
                              <HStack spacing={2}>
                                {getRankIcon(rank)}
                              </HStack>
                            </Td>

                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={userData.displayName}
                                />
                                <VStack align="start" spacing={0}>
                                  <Text
                                    fontWeight="medium"
                                    color={isCurrentUser ? 'blue.200' : 'white'}
                                  >
                                    {userData.displayName}
                                    {isCurrentUser && (
                                      <Badge ml={2} size="sm" colorScheme="blue">
                                        You
                                      </Badge>
                                    )}
                                  </Text>
                                  <Text fontSize="xs" color="gray.400">
                                    Since {userData.joinDate?.toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short'
                                    })}
                                  </Text>
                                </VStack>
                              </HStack>
                            </Td>

                            <Td isNumeric>
                              <Text color="green.400" fontWeight="medium">
                                {userData.correctPredictions}
                              </Text>
                            </Td>

                            <Td isNumeric>
                              <VStack spacing={1} align="end">
                                <Text
                                  fontWeight="bold"
                                  color={getAccuracyColor(userData.accuracy)}
                                >
                                  {userData.accuracy.toFixed(1)}%
                                </Text>
                                <Progress
                                  value={userData.accuracy}
                                  colorScheme={userData.accuracy >= 70 ? 'green' : userData.accuracy >= 50 ? 'yellow' : 'red'}
                                  size="sm"
                                  w="60px"
                                />
                              </VStack>
                            </Td>

                            <Td isNumeric>
                              <Text color="white" fontWeight="medium">
                                {userData.totalVotes}
                              </Text>
                            </Td>

                            <Td isNumeric>
                              <Text color="gray.300">
                                {userData.eventsParticipated}
                              </Text>
                            </Td>

                            <Td>
                              {getRankBadge(rank)}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box textAlign="center" py={10}>
              <Icon as={FiUsers} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.400" fontSize="lg">
                No leaderboard data available
              </Text>
              <Text color="gray.500" fontSize="sm">
                Start voting to see rankings!
              </Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* User Profile Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={isMobile ? "full" : "6xl"}
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>User Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedUser && (
              <UserProfile userId={selectedUser.userId} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Leaderboard;
